import {useProjectId} from "../ProjectName";
import {api} from "../../../../api/API";
import {useGetAll} from "../../../logistics/routes/useGetAll";
import {Loader} from "../../../../misc/Loader";
import {Opening} from "../../../../api/Openings";
import {tableBorderColor, tableClass} from "./library/ShortCodes";
import {IconButton2, IconCell} from "./library/IconCell";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AssignmentIcon from '@mui/icons-material/Assignment';
import {ProductLookup} from "../extras/ProductLookup";
import {GroupNumberLookup} from "./GroupNumberLookup";
import {actionColWidth, toHwOpening} from "./HardwareGroup";
import {indentWidth} from "./HardwareTable";
import {useReload, useReloader} from "./ReloadContext";
import {Product, ProductSearchResult} from "../../../../api/Products";
import {ShortCode} from "../../../../api/Library";
import {useSnackAction} from "./SnackAction";
import {useHwAPI} from "./HardwareGeneric";
import {useIsQuote} from "../../quote/QuoteContainer";
import {useSyncedRef} from "../../../../misc/SyncedRef";
import {QuoteOpening} from "../../../../api/QuoteOpenings";
import {useAlternative} from "../../quote/alternative/Alternative";
import {useContext, useRef, useState} from "react";
import {EditHardwareGroupWithContent, EditHardwareItem, HwEditingContext} from "./HwEditingContextProvider";
import React from "react";
import {useSnackbar} from "../../../../misc/Snackbar";
import {cellInActive} from "../pricing/cellconst";
import {ConfirmShortcodeChange} from "../../../../api/QuoteHardware";
import {ResolveShortcodeChangesDialog} from "./ResolveShortcodeChangesDialog";
import {useAsyncAction} from "nate-react-api-helpers";
import {Paginated} from "../../../../api/Users";
import {makeNumberOrUndefined} from "../../quote/openings/util";

export function PendingHardware(props: {
    search: string
}) {
    const project = useProjectId();
    const isQuote = useIsQuote();
    const isQuoteRef = useSyncedRef(isQuote)
    const alternative = useAlternative();

    const list = useGetAll<QuoteOpening, {
        project: number,
        alternative: number,
        type: any
    }>(async (input) => {
        if (isQuoteRef.current) return await api.quoteOpenings.list(input)
        return await api.openings.list(input) as any as Paginated<QuoteOpening>;
    }, {
            project: project,
            alternative: alternative,
            type: "have-no-hardware-group" as "have-no-hardware-group",
        }, [project, alternative]);

    useReload(list.reload, [])
    const parentOnChangeRef = useReloader();
    const hapi = useHwAPI();
    const editCtx = useContext(HwEditingContext);
    const onCreate = useSyncedRef(editCtx.onCreate);
    const [showShortcodeDetail, setShowShortcodeDetail] = useState<ConfirmShortcodeChange>();
    const lastUpdateCallbackValue = useRef<EditHardwareGroupWithContent>();

    const update = useAsyncAction(async (input: {
        value: EditHardwareGroupWithContent;
        createShortcode?: any;
        updateShortcode?: any;
        linkToShortcode?: any;
    }) => {

        snack.loading();

        try {
            lastUpdateCallbackValue.current = input.value;

            const resp = await hapi.upsert({
                value: input.value,
                createShortcode: input.createShortcode,
                updateShortcode: input.updateShortcode,
                linkToShortcode: makeNumberOrUndefined(input.linkToShortcode),
            });

            if (!resp.updated) {
                setShowShortcodeDetail(resp.confirmShortcodeChanges)
                snack.hide();
                return;
            }

            snack.success("Updated");
            setShowShortcodeDetail(undefined)
            parentOnChangeRef.current();
        } catch (e: any) {
            snack.error(e.toString());
            throw e;
        }

    }, []);

    const updateCb = useRef(update.callback)
    updateCb.current = update.callback;

    const makeGroupAndAssignOpening = useSnackAction(async (input: {
        opening: Opening;
        groupContent: ProductSearchResult | ShortCode;
    }) => {
        const op = input.opening;
        const content = input.groupContent;

        if(isShortCode(content)) {
            throw new Error("should have called the shortcode updater")
            return;
        }

        const product = await api.products.getById(content.id)
        let products: EditHardwareItem[] = toProductQty(product, op.openingType);

        // need to edit the group to build shortcode before submitting

        onCreate.current({
            id: 0,
            project: project,
            quoteAlternative: alternative,
            openings: [toHwOpening(op)],
            hardware: products,
            flipDoubleActiveHanding: false,
            notes: "",
        })
    }, {
        success: "Updated",
    });

    const skipOpenings = editCtx.creating ? editCtx.creating.openings.map(o => o.id) : [];
    const snack = useSnackbar();

    return (
        <div style={{flex: 1, background: cellInActive}}>
            <div style={{fontSize: "0.9rem", fontWeight: 600, padding: 16, paddingLeft: actionColWidth + 3, paddingBottom: 4}}>Pending</div>
            {showShortcodeDetail && <ResolveShortcodeChangesDialog
                value={showShortcodeDetail}
                error={update.error}
                errorDetail={update.error && update.raw ? update.raw.details : null}
                loading={update.loading}
                onCancel={() => setShowShortcodeDetail(undefined)}
                onDone={(input) => {
                    const value = lastUpdateCallbackValue.current;
                    if(!value) return;

                    update.callback({
                        value: value, // use last callback, b/c we may call update for a duplicate action which uses a different value than the "state" value.
                        createShortcode: input.createShortcode,
                        updateShortcode: input.updateShortcode,
                        linkToShortcode: makeNumberOrUndefined(input.linkToShortcode),
                    })
                }} />}
            <Loader<QuoteOpening[]> {...list}>
                {dt => {
                    const pending = (dt || []).filter(d => {
                        if(!props.search) return true;
                        return d.name.toLowerCase().indexOf(props.search.toLowerCase()) !== -1
                    });

                    return (<table className={tableClass}>
                        <tbody>
                        {pending.length === 0 && <tr><td style={{width: actionColWidth}} /><td><i>No pending openings</i></td></tr>}
                        {pending
                            .filter(i => skipOpenings.indexOf(i.id) === -1)
                            .map((p: QuoteOpening) => <React.Fragment key={p.id}>
                                <tr key={"header" + p.id} style={{borderTop: "1px solid " + tableBorderColor, borderBottomColor: "transparent"}}>
                                    <td style={{width: actionColWidth}} />
                                    <td colSpan={2} style={{fontSize: "0.8rem"}}>
                                        {p.name} {p.doorElevation} Door - {p.locationOne} {p.locationTransition} {p.locationTwo} ({p.qty}) {p.hwDescriptiveName}
                                    </td>
                                </tr>
                                <tr key={"add" + p.id} style={{borderColor: "transparent"}}>
                                    <IconCell>
                                        <IconButton2>
                                            <AddCircleOutlineIcon fontSize="inherit" />
                                        </IconButton2>
                                    </IconCell>
                                    <td style={{width: indentWidth}}></td>
                                    <td className="no-pad">
                                        <ProductLookup
                                            kind="hardware"
                                            placeholder="Create new group by entering Shortcode or product name"
                                            onSelect={async (result: ProductSearchResult | ShortCode) => {
                                                if(isShortCode(result)) {
                                                    updateCb.current({
                                                        value: {
                                                            id: 0,
                                                            name: "",
                                                            openings: [toHwOpening(p)],
                                                            hardware: toProductQty(result, p.openingType),
                                                            project: project,
                                                            quoteAlternative: alternative,
                                                            flipDoubleActiveHanding: false,
                                                            notes: "",
                                                        },

                                                        linkToShortcode: makeNumberOrUndefined(result.id),
                                                    });

                                                    return;
                                                }

                                                makeGroupAndAssignOpening.callback({
                                                    opening: p,
                                                    groupContent: result,
                                                })
                                            }}
                                            includeShortCodesForProject={project}
                                        />
                                    </td>
                                </tr>
                                <tr className={p.id + "assign"}>
                                    <IconCell>
                                        <IconButton2>
                                            <AssignmentIcon fontSize="inherit" />
                                        </IconButton2>
                                    </IconCell>
                                    <td></td>
                                    <td className="no-pad">
                                        <GroupNumberLookup
                                            onAssign={async id => {
                                                try {
                                                    snack.loading();

                                                    await hapi.updateHardwareGroupLink({
                                                        group: id,
                                                        opening: p.id,
                                                        link: true,
                                                    })

                                                    snack.success("Updated");
                                                    parentOnChangeRef.current();
                                                } catch(e: any) {
                                                    snack.error(e.message);
                                                    parentOnChangeRef.current();
                                                }
                                            }}
                                            onCreate={name => {
                                                editCtx.onCreate({
                                                    id: 0,
                                                    project: project,
                                                    quoteAlternative: alternative,
                                                    openings: [p],
                                                    hardware: [],
                                                    name: name,
                                                    flipDoubleActiveHanding: false,
                                                    notes: "",
                                                })
                                            }} />
                                    </td>
                                </tr>
                            </React.Fragment>)}
                        </tbody>
                    </table>)
                }}
            </Loader>
        </div>
    )
}

export function getHwOpeningType(openingType: string | undefined, isCom: boolean | undefined) {
    if(isCom) {
        return "Pair";
    }

    return openingType;
}

export function toProductQty(content: ShortCode | Product, hwOpeningType?: string): EditHardwareItem[] {

    if(isShortCode(content)) {
        const bothPair = content.openingType === "Pair" && hwOpeningType === "Pair";
        const singleToPair =  content.openingType === "Single" && hwOpeningType === "Pair";
        const pairToSingle = content.openingType === "Pair" && hwOpeningType === "Single";

        return (content.items||[]).map(i => {
            let common = 0;
            let active = 0;
            let inactive = 0;

            switch(i.target) {
                default:
                case "active":
                    active = i.qty;
                    break;
                case "inactive":
                    inactive = i.qty;
                    break;
                case "common":
                    common = i.qty;
                    break;
                case "both":
                    if(bothPair) {
                        active = Math.round(i.qty/2);
                        inactive = active;
                    } else if(singleToPair) {
                        active = i.qty;
                        inactive = active;
                    } else if(pairToSingle) {
                        active = Math.ceil(i.qty / 2);
                        inactive = 0;
                    } else {
                        active = i.qty;

                        if(hwOpeningType === "Pair" || !hwOpeningType) {
                            inactive = active;
                        }
                    }

                    break;
            }


            return ({
                product: i.product,
                finish: i.finish,
                finishName: "",
                name: i.productName,
                manufacturer: i.manufacturer,
                manufacturerName: i.manufacturerName,
                manufacturerShortName: "",

                productCode: i.productCode,
                dimensions: "",
                productIsAutoQtyHinge: i.productIsAutoQtyHinge,

                activeQty: active,
                inactiveQty: inactive,
                commonQty: common,
                calcTotalQty: 0,
                calcByDimension: [],

                dimHeight: i.dimHeight,
                dimWidth: "",
                dimLength: "",
                category: "",

                overrideHingeActiveQty: null,
                overrideHingeInActiveQty: null,

                shortcodeItem: i.id,

                // server recomputes customerSupplied from supplierCategory on upsert
                customerSupplied: false,
                supplierCategory: i.defaultSupplierCategory,
                supplierCategoryName: i.defaultSupplierCategoryName,

                archived: false,
                isCustom: false,
                note: "",
                discontinuedReplaceWith: [],
                discontinued: false,
                electricStrikeConfig: {},
                warnings: null,
            })
        })
    } else {
        const defaultQty = Math.max(content.defaultQty, 1);
        const active = content.defaultHwGroup === "active" || content.defaultHwGroup === "each-door" ? defaultQty : 0
        const inactive = content.defaultHwGroup === "inactive" || content.defaultHwGroup === "each-door" ? defaultQty : 0
        const common = content.defaultHwGroup === "common" ? defaultQty : 0

        return [{

            product: content.id,
            finish: content.finish,
            finishName: content.finishName,
            name: content.categoryName,

            category: content.categoryName,

            manufacturer: content.manufacturer,
            manufacturerName: content.manufacturerName,
            manufacturerShortName: "",

            productCode: content.productCode,
            dimensions: content.dimensions,
            dimWidth: content.dimWidth,
            dimHeight: content.dimHeight,
            dimLength: content.dimLength,
            productIsAutoQtyHinge: content.isAutoQtyHinge,

            // server recomputes customerSupplied from supplierCategory on upsert
            customerSupplied: false,
            supplierCategory: content.defaultSupplierCategory,
            supplierCategoryName: content.defaultSupplierCategoryName,

            activeQty: active,
            inactiveQty: inactive,
            commonQty: common,

            calcTotalQty: 0,
            calcByDimension: [],
            overrideHingeActiveQty: null,
            overrideHingeInActiveQty: null,

            note: "",
            archived: false,
            isCustom: false,

            discontinued: content.discontinued,
            discontinuedReplaceWith: content.discontinuedReplaceWith,
            electricStrikeConfig: {},
            warnings: null,
        }]
    }
}

export function isShortCode(input: any): input is ShortCode {
    return typeof input === "object" && input.hasOwnProperty("id") && input.hasOwnProperty("value") && input.hasOwnProperty("library")
}